/**
 * Toast Notification Service
 * Provides user-visible notifications for errors, successes, and info messages.
 * Uses Angular signals for reactive state management.
 */

import { Injectable, signal, computed } from '@angular/core';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  title?: string;
  duration: number;
  dismissible: boolean;
  timestamp: number;
}

export interface ToastOptions {
  title?: string;
  duration?: number;
  dismissible?: boolean;
}

const DEFAULT_DURATION = 5000; // 5 seconds
const ERROR_DURATION = 8000; // 8 seconds for errors (more time to read)
const MAX_TOASTS = 5; // Maximum toasts visible at once

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private _toasts = signal<Toast[]>([]);
  private timeoutIds = new Map<string, ReturnType<typeof setTimeout>>();

  // Public readonly signal
  readonly toasts = this._toasts.asReadonly();

  // Computed: check if there are any toasts
  readonly hasToasts = computed(() => this._toasts().length > 0);

  /**
   * Show a success toast
   */
  success(message: string, options?: ToastOptions): string {
    return this.show(message, 'success', {
      title: options?.title ?? 'Success',
      duration: options?.duration ?? DEFAULT_DURATION,
      dismissible: options?.dismissible ?? true,
    });
  }

  /**
   * Show an error toast
   */
  error(message: string, options?: ToastOptions): string {
    return this.show(message, 'error', {
      title: options?.title ?? 'Error',
      duration: options?.duration ?? ERROR_DURATION,
      dismissible: options?.dismissible ?? true,
    });
  }

  /**
   * Show a warning toast
   */
  warning(message: string, options?: ToastOptions): string {
    return this.show(message, 'warning', {
      title: options?.title ?? 'Warning',
      duration: options?.duration ?? DEFAULT_DURATION,
      dismissible: options?.dismissible ?? true,
    });
  }

  /**
   * Show an info toast
   */
  info(message: string, options?: ToastOptions): string {
    return this.show(message, 'info', {
      title: options?.title ?? 'Info',
      duration: options?.duration ?? DEFAULT_DURATION,
      dismissible: options?.dismissible ?? true,
    });
  }

  /**
   * Show a toast with custom type
   */
  show(
    message: string,
    type: ToastType,
    options?: ToastOptions
  ): string {
    const id = this.generateId();
    const duration = options?.duration ?? DEFAULT_DURATION;

    const toast: Toast = {
      id,
      message,
      type,
      title: options?.title,
      duration,
      dismissible: options?.dismissible ?? true,
      timestamp: Date.now(),
    };

    // Add toast to the list
    this._toasts.update((toasts) => {
      // Remove oldest if we exceed max
      const newToasts = [...toasts, toast];
      if (newToasts.length > MAX_TOASTS) {
        const removed = newToasts.shift();
        if (removed) {
          this.clearTimeout(removed.id);
        }
      }
      return newToasts;
    });

    // Auto-dismiss after duration
    if (duration > 0) {
      const timeoutId = setTimeout(() => {
        this.dismiss(id);
      }, duration);
      this.timeoutIds.set(id, timeoutId);
    }

    return id;
  }

  /**
   * Dismiss a specific toast by ID
   */
  dismiss(id: string): void {
    this.clearTimeout(id);
    this._toasts.update((toasts) => toasts.filter((t) => t.id !== id));
  }

  /**
   * Dismiss all toasts
   */
  dismissAll(): void {
    this.timeoutIds.forEach((_, id) => this.clearTimeout(id));
    this._toasts.set([]);
  }

  /**
   * Show HTTP error with user-friendly message
   */
  httpError(status: number, serverMessage?: string): string {
    const { title, message } = this.getHttpErrorMessage(status, serverMessage);
    return this.error(message, { title });
  }

  /**
   * Get user-friendly error message for HTTP status codes
   */
  private getHttpErrorMessage(
    status: number,
    serverMessage?: string
  ): { title: string; message: string } {
    switch (status) {
      case 0:
        return {
          title: 'Connection Error',
          message:
            'Unable to connect to the server. Please check your internet connection and try again.',
        };
      case 400:
        return {
          title: 'Invalid Request',
          message: serverMessage || 'The request was invalid. Please check your input and try again.',
        };
      case 401:
        return {
          title: 'Authentication Required',
          message: 'Your session has expired. Please sign in again.',
        };
      case 403:
        return {
          title: 'Access Denied',
          message: "You don't have permission to perform this action.",
        };
      case 404:
        return {
          title: 'Not Found',
          message: 'The requested resource was not found.',
        };
      case 408:
        return {
          title: 'Request Timeout',
          message:
            'The server took too long to respond. Please try again.',
        };
      case 429:
        return {
          title: 'Too Many Requests',
          message: 'You have made too many requests. Please wait a moment and try again.',
        };
      case 500:
        return {
          title: 'Server Error',
          message:
            'An unexpected error occurred on the server. Please try again later.',
        };
      case 502:
        return {
          title: 'Bad Gateway',
          message:
            'The server received an invalid response. Please try again later.',
        };
      case 503:
        return {
          title: 'Service Unavailable',
          message:
            'The service is temporarily unavailable. Please try again later.',
        };
      case 504:
        return {
          title: 'Gateway Timeout',
          message:
            'The server took too long to respond. Please try again.',
        };
      default:
        if (status >= 500) {
          return {
            title: 'Server Error',
            message:
              serverMessage || 'An unexpected server error occurred. Please try again later.',
          };
        }
        return {
          title: 'Error',
          message: serverMessage || 'An unexpected error occurred. Please try again.',
        };
    }
  }

  private generateId(): string {
    return `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  private clearTimeout(id: string): void {
    const timeoutId = this.timeoutIds.get(id);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.timeoutIds.delete(id);
    }
  }
}
